import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Checkbox,
    FormControlLabel,
    Modal as MuiModal,
    Backdrop
} from '@mui/material';
import { GroupsOutlinedIcon, HighlightOffOutlinedIcon, InfoOutlinedIcon, CloseIcon } from 'icons/materialIcons';
import { useCreateTeamMember } from 'hooks/useCreateTeamMember';
import { useNavigate } from 'react-router-dom';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';

const TeamAddByEmailContent = ({ onDataChange, onValidationChange }) => {
    const navigate = useNavigate();
    const [emailInput, setEmailInput] = useState('');
    const [validEmails, setValidEmails] = useState([]);
    const [invalidEmails, setInvalidEmails] = useState([]);
    const [sendInvites, setSendInvites] = useState(true);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

    // ✅ Match individual plan pattern - separate snackbar state variables
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Use the custom hook
    const { createMultipleTeamMembers, loading, error } = useCreateTeamMember();

    const validateEmails = (emailString) => {
        if (!emailString.trim()) {
            return { valid: [], invalid: [] };
        }

        const emailList = emailString.split(',').map(email => email.trim()).filter(email => email);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const validEmails = [];
        const invalidEmails = [];

        emailList.forEach(email => {
            if (emailRegex.test(email)) {
                validEmails.push(email.toLowerCase());
            } else {
                invalidEmails.push(email);
            }
        });

        return { valid: validEmails, invalid: invalidEmails };
    };

    const handleEmailInputChange = (event) => {
        const value = event.target.value;
        setEmailInput(value);

        // If user clears the input while typing, immediately reset state
        if (!value.trim()) {
            setValidEmails([]);
            setInvalidEmails([]);
            onDataChange({ emails: [], sendInvites });
            onValidationChange(false);
        }
    };

    const handleEmailInputBlur = () => {
        // If input is empty, clear invalid emails but keep valid ones
        if (!emailInput.trim()) {
            setInvalidEmails([]);
            return;
        }

        const result = validateEmails(emailInput);

        // Append new valid emails to existing ones (avoid duplicates)
        const newValidEmails = [...validEmails];
        result.valid.forEach(email => {
            if (!newValidEmails.includes(email)) {
                newValidEmails.push(email);
            }
        });

        // Append new invalid emails to existing ones (avoid duplicates)
        const newInvalidEmails = [...invalidEmails];
        result.invalid.forEach(email => {
            if (!newInvalidEmails.includes(email)) {
                newInvalidEmails.push(email);
            }
        });

        setValidEmails(newValidEmails);
        setInvalidEmails(newInvalidEmails);
        onDataChange({ emails: newValidEmails, sendInvites });
        onValidationChange(newValidEmails.length > 0);

        // Clear the input field after processing emails into chips
        setEmailInput('');
    };

    const handleSendInvitesChange = (event) => {
        const checked = event.target.checked;
        setSendInvites(checked);
        onDataChange({ emails: validEmails, sendInvites: checked });
    };

    const handleRemoveEmail = (emailToRemove) => {
        const updatedEmails = validEmails.filter(email => email !== emailToRemove);
        setValidEmails(updatedEmails);
        onDataChange({ emails: updatedEmails, sendInvites });
        onValidationChange(updatedEmails.length > 0);
    };

    const handleAddTeammatesClick = () => {
        setIsConfirmationModalOpen(true);
    };

    const handleModalClose = () => {
        setIsConfirmationModalOpen(false);
    };

    const handleAddWithoutInvites = async () => {
        try {
            console.log('🔵 Starting invitation for emails:', validEmails);
            const result = await createMultipleTeamMembers(validEmails, false);
            console.log('🔵 Invitation result:', result);

            // ✅ ALWAYS close modal after operation completes
            setIsConfirmationModalOpen(false);

            // Show success message
            if (result.successCount > 0) {
                // ✅ Show detailed error messages for failed invitations
                let message;
                if (result.failedCount > 0) {
                    // Get specific error messages
                    const failedDetails = result.failed.map(f => `${f.email}: ${f.error}`).join('; ');
                    message = `Successfully invited ${result.successCount} member(s). ${result.failedCount} failed: ${failedDetails}`;
                } else {
                    message = `Successfully invited ${result.successCount} member(s)!`;
                }

                setSnackbarMessage(message);
                setSnackbarSeverity(result.failedCount > 0 ? 'warning' : 'success');
                setSnackbarOpen(true);

                // Clear the form
                setValidEmails([]);
                setEmailInput('');
                onDataChange({ emails: [], sendInvites });
                onValidationChange(false);

                // Navigate after short delay to show message
                setTimeout(() => {
                    navigate('/team/members');
                }, 1500);
            } else {
                // ✅ All invitations failed - show specific errors (DON'T navigate, DON'T clear form)
                const failedDetails = result.failed.map(f => `${f.email}: ${f.error}`).join('; ');
                const errorMessage = failedDetails || 'Failed to invite team members. Please try again.';
                console.log('🔴 All invitations failed. Error message:', errorMessage);
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                console.log('🔴 Snackbar state set:', { open: true, message: errorMessage, severity: 'error' });
            }
        } catch (err) {
            console.error('🔴 Catch block error:', err);
            // ✅ Close modal on error too
            setIsConfirmationModalOpen(false);
            setSnackbarMessage(err.message || 'Failed to invite team members');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleAddAndSendInvites = async () => {
        try {
            console.log('🔵 Starting invitation for emails:', validEmails);
            const result = await createMultipleTeamMembers(validEmails, true);
            console.log('🔵 Invitation result:', result);

            // ✅ ALWAYS close modal after operation completes
            setIsConfirmationModalOpen(false);

            // Show success message
            if (result.successCount > 0) {
                // ✅ Show detailed error messages for failed invitations
                let message;
                if (result.failedCount > 0) {
                    // Get specific error messages
                    const failedDetails = result.failed.map(f => `${f.email}: ${f.error}`).join('; ');
                    message = `Successfully invited ${result.successCount} member(s). ${result.failedCount} failed: ${failedDetails}`;
                } else {
                    message = `Successfully invited ${result.successCount} member(s)!`;
                }

                setSnackbarMessage(message);
                setSnackbarSeverity(result.failedCount > 0 ? 'warning' : 'success');
                setSnackbarOpen(true);

                // Clear the form
                setValidEmails([]);
                setEmailInput('');
                onDataChange({ emails: [], sendInvites });
                onValidationChange(false);

                // Navigate after short delay to show message
                setTimeout(() => {
                    navigate('/team/members');
                }, 1500);
            } else {
                // ✅ All invitations failed - show specific errors (DON'T navigate, DON'T clear form)
                const failedDetails = result.failed.map(f => `${f.email}: ${f.error}`).join('; ');
                const errorMessage = failedDetails || 'Failed to invite team members. Please try again.';
                console.log('🔴 All invitations failed. Error message:', errorMessage);
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                console.log('🔴 Snackbar state set:', { open: true, message: errorMessage, severity: 'error' });
            }
        } catch (err) {
            console.error('🔴 Catch block error:', err);
            // ✅ Close modal on error too
            setIsConfirmationModalOpen(false);
            setSnackbarMessage(err.message || 'Failed to invite team members');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // ✅ Match individual plan pattern - simple close handler
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    return (
        <Box sx={{
            padding: '2rem',
            paddingTop: '3rem',
            maxWidth: '700px' // Reduce content width
        }}>
            {/* Heading */}
            <Typography variant="body2" sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#4f4f4f',
                marginBottom: '1.25rem'
            }}>
                Invite Team Members
            </Typography>

            {/* Description */}
            <Typography variant="body1" sx={{
                marginBottom: '1.5rem',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#828282',
                lineHeight: 1.5
            }}>
                Add members to your team via email. If a touch user with that email already exists,
                the member will be pending until the user accepts the email invite.
            </Typography>

            {/* Email Input Box with Styled Email Chips */}
            <Box sx={{
                marginBottom: '1rem',
                backgroundColor: '#f7f7f7',
                borderRadius: '0.25rem',
                padding: '0.5rem 0.75rem',
                minHeight: '3.5rem',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '0.375rem',
                cursor: 'text',
                '&:focus-within': {
                    outline: '0.125rem solid #1976d2',
                    outlineOffset: '-0.125rem'
                }
            }}
                onClick={() => document.getElementById('email-input-field').focus()}
            >
                {/* Styled Email Chips */}
                {validEmails.map((email, index) => (
                    <Box
                        key={index}
                        sx={{
                            backgroundColor: '#e0e0e0',
                            borderRadius: '0.75rem',
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#828282',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.375rem'
                        }}
                    >
                        <span>{email}</span>
                        <HighlightOffOutlinedIcon
                            sx={{
                                fontSize: '1.25rem',
                                color: '#555',
                                cursor: 'pointer',
                                '&:hover': {
                                    color: '#333'
                                }
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveEmail(email);
                            }}
                        />
                    </Box>
                ))}

                {/* Hidden Input Field */}
                <input
                    id="email-input-field"
                    type="text"
                    placeholder={validEmails.length === 0 ? "Enter emails separated by commas" : ""}
                    value={emailInput}
                    onChange={handleEmailInputChange}
                    onBlur={handleEmailInputBlur}
                    onKeyDown={(e) => {
                        // Prevent backspace from deleting email chips when input is empty
                        if (e.key === 'Backspace' && emailInput === '' && validEmails.length > 0) {
                            e.preventDefault();
                        }
                    }}
                    style={{
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        fontSize: '0.8rem',
                        fontWeight: '300',
                        color: '#333',
                        flex: 1,
                        minWidth: '7.5rem'
                    }}
                />
            </Box>

            {/* Blue Summary Section */}
            {validEmails.length > 0 && (
                <Box sx={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#E3F2FD',
                    borderRadius: '0.5rem',
                    margin: '1rem 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <GroupsOutlinedIcon sx={{
                            color: '#1976d2',
                            fontSize: '1rem',
                            marginRight: '0.75rem'
                        }} />
                        <Typography variant="body1" sx={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#828282',
                            lineHeight: 1.5
                        }}>
                            Adding {validEmails.length} member{validEmails.length !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                    <Typography variant="body1" sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#828282',
                        lineHeight: 1.5
                    }}>
                        This will influence your monthly payment
                    </Typography>
                </Box>
            )}

            {/* Invalid Emails Warning */}
            {invalidEmails.length > 0 && (
                <Box sx={{
                    backgroundColor: '#ffebee',
                    border: '0.0625rem solid #ffcdd2',
                    borderRadius: '0.5rem',
                    padding: '0.75rem 1rem',
                    marginBottom: '1.5rem',
                    position: 'relative'
                }}>
                    <HighlightOffOutlinedIcon
                        sx={{
                            position: 'absolute',
                            top: '0.5rem',
                            right: '0.5rem',
                            fontSize: '1.25rem',
                            color: '#c62828',
                            cursor: 'pointer',
                            '&:hover': {
                                color: '#b71c1c'
                            }
                        }}
                        onClick={() => setInvalidEmails([])}
                    />
                    <Typography variant="body2" sx={{
                        color: '#c62828',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        marginBottom: '0.25rem',
                        paddingRight: '2rem'
                    }}>
                        Invalid email format{invalidEmails.length !== 1 ? 's' : ''}:
                    </Typography>
                    <Typography variant="body2" sx={{
                        color: '#d32f2f',
                        fontSize: '0.85rem',
                        paddingRight: '2rem'
                    }}>
                        {invalidEmails.join(', ')}
                    </Typography>
                </Box>
            )}

            {/* Bottom Section: Checkbox and Button */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '2rem'
            }}>
                {/* Left: Send Email Invites Checkbox */}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={sendInvites}
                            onChange={handleSendInvitesChange}
                            sx={{
                                color: '#e0e0e0',
                                '&.Mui-checked': {
                                    color: '#000',
                                },
                                '& .MuiSvgIcon-root': {
                                    borderRadius: '0.375rem',
                                },
                            }}
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
                            <Typography variant="body2" sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#828282'
                            }}>
                                Send email invites
                            </Typography>
                            <InfoOutlinedIcon sx={{
                                fontSize: '1.25rem',
                                color: '#999',
                                cursor: 'pointer'
                            }} />
                        </Box>
                    }
                />

                {/* Right: Add Teammates Button */}
                <Button
                    variant="contained"
                    disabled={validEmails.length === 0 || loading}
                    onClick={handleAddTeammatesClick}
                    sx={{
                        backgroundColor: '#000',
                        color: '#fff',
                        fontWeight: 600,
                        borderRadius: '1.25rem',
                        textTransform: 'none',
                        padding: '0.5rem 1.5rem',
                        fontSize: '0.875rem',
                        minWidth: '8rem',
                        height: '2.5rem',
                        '&:hover': {
                            backgroundColor: '#333',
                        },
                        '&:disabled': {
                            backgroundColor: '#e0e0e0',
                            color: '#999',
                        }
                    }}
                >
                    {loading ? 'Adding...' : 'Add Teammates'}
                </Button>
            </Box>

            {/* Confirmation Modal */}
            <MuiModal
                open={isConfirmationModalOpen}
                onClose={handleModalClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 500,
                        maxWidth: '90%',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 8,
                        position: 'relative'
                    }}
                >
                    {/* Close Icon */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '1rem',
                            right: '1rem',
                            cursor: 'pointer',
                            zIndex: 1
                        }}
                        onClick={handleModalClose}
                    >
                        <CloseIcon sx={{
                            fontSize: '1.5rem',
                            color: '#666',
                            '&:hover': {
                                color: '#333'
                            }
                        }} />
                    </Box>

                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            textAlign: 'left',
                            mb: 1,
                            paddingRight: '2rem'
                        }}
                    >
                        Send email invites?
                    </Typography>

                    <Typography
                        sx={{
                            fontWeight: 'normal',
                            fontSize: '0.875rem',
                            textAlign: 'left',
                            mb: 3,
                            color: '#666'
                        }}
                    >
                        Are you sure you want to send email invites to all newly added members?
                    </Typography>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2
                    }}>
                        <Button
                            variant="outlined"
                            onClick={handleAddWithoutInvites}
                            disabled={loading}
                            sx={{
                                backgroundColor: 'white',
                                color: '#4f4f4f',
                                borderRadius: '1.25rem',
                                border: '1px solid #e3e3e3',
                                textTransform: 'none',
                                padding: '0.5rem 2rem',
                                minWidth: '180px',
                                height: '40px',
                                '&:disabled': {
                                    backgroundColor: '#f5f5f5',
                                    color: '#999',
                                    cursor: 'not-allowed'
                                }
                            }}
                        >
                            {loading ? 'Adding...' : 'Add without invites'}
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleAddAndSendInvites}
                            disabled={loading}
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                borderRadius: '1.25rem',
                                textTransform: 'none',
                                padding: '0.5rem 2rem',
                                minWidth: '180px',
                                height: '40px',
                                '&:disabled': {
                                    backgroundColor: '#ccc',
                                    color: '#666',
                                    cursor: 'not-allowed'
                                }
                            }}
                        >
                            {loading ? 'Adding...' : 'Add and send invites'}
                        </Button>
                    </Box>
                </Box>
            </MuiModal>

            {/* ✅ Success/Error Notification - Match individual plan pattern */}
            <TeamNotificationSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                handleClose={handleSnackbarClose}
                severity={snackbarSeverity}
            />
        </Box>
    );
};

export default TeamAddByEmailContent;
